import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../../context/I18nContext";
import { pathFor, switchLangPath, LANGS } from "../../lib/routes";
import { Menu, X } from "lucide-react";
import { cn } from "../../lib/utils";

const anchorId = {
  about: "about",
  how: "how",
  benefits: "benefits",
  faq: "faq",
  newsletter: "newsletter",
};

export default function Header() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isHome =
    location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  const goToAnchor = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    } else {
      navigate(pathFor("home", lang) + `#${id}`);
    }
  };

  const switchLang = (target) => {
    if (target === lang) return;
    const nextPath = switchLangPath(location.pathname, target);
    navigate(nextPath + location.hash, { replace: false });
  };

  const navItems = [
    { key: "about", label: t("nav.about"), href: `#${anchorId.about}` },
    { key: "how", label: t("nav.how"), href: `#${anchorId.how}` },
    { key: "benefits", label: t("nav.benefits"), href: `#${anchorId.benefits}` },
    { key: "faq", label: t("nav.faq"), href: `#${anchorId.faq}` },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-[#0F172A] text-white border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="flex h-16 items-center justify-between">
          <Link
            to={pathFor("home", lang)}
            data-testid="header-brand-link"
            className="flex items-center gap-2 no-underline"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-300 font-bold"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Sb
            </span>
            <span
              className="text-white font-semibold text-lg tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t("common.brand")}
              <span className="ml-1 text-xs font-medium uppercase text-emerald-300 tracking-widest">
                {t("common.brandTag")}
              </span>
            </span>
          </Link>

          <nav
            className="hidden md:flex items-center gap-8"
            aria-label="Primary"
          >
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={goToAnchor(item.key)}
                data-testid={`nav-${item.key}-link`}
                className="text-sm text-slate-200 hover:text-white no-underline"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <div
              role="group"
              aria-label={t("lang.switchTo")}
              className="flex items-center rounded-full border border-white/15 bg-white/5 p-0.5"
              data-testid="lang-switcher"
            >
              {LANGS.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => switchLang(l)}
                  data-testid={`lang-switch-${l}`}
                  className={cn(
                    "px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full",
                    lang === l
                      ? "bg-white text-slate-900"
                      : "text-slate-200 hover:text-white",
                  )}
                >
                  {l === "ec" ? "ES" : "EN"}
                </button>
              ))}
            </div>
            <a
              href={`#${anchorId.newsletter}`}
              onClick={goToAnchor("newsletter")}
              data-testid="header-cta-newsletter"
              className="inline-flex items-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400 no-underline"
            >
              {t("nav.newsletter")}
            </a>
          </div>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-white/10"
            aria-label={open ? t("common.closeMenu") : t("common.openMenu")}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div
            className="md:hidden border-t border-white/10 pb-5 pt-3"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile primary">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={goToAnchor(item.key)}
                  data-testid={`mobile-nav-${item.key}-link`}
                  className="px-2 py-3 text-base text-slate-100 hover:text-white no-underline"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={`#${anchorId.newsletter}`}
                onClick={goToAnchor("newsletter")}
                data-testid="mobile-header-cta-newsletter"
                className="mt-2 inline-flex items-center justify-center rounded-md bg-emerald-500 px-4 py-3 text-sm font-semibold text-slate-900 no-underline"
              >
                {t("nav.newsletter")}
              </a>
              <div
                role="group"
                aria-label={t("lang.switchTo")}
                className="mt-3 flex items-center gap-2"
              >
                {LANGS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => switchLang(l)}
                    data-testid={`mobile-lang-switch-${l}`}
                    className={cn(
                      "px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full border",
                      lang === l
                        ? "bg-white text-slate-900 border-white"
                        : "border-white/20 text-slate-200",
                    )}
                  >
                    {l === "ec" ? "ES" : "EN"}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
